// frontend/src/components/LogicBuilder.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, X } from "lucide-react";
import { 
  QuestionType, 
  SkipLogicRule, 
  LogicCondition, 
  LogicOperator, 
  SkipAction, 
  TerminationType,
  getSelectableOptions,
} from "@/types/survey";

// Question types that have selectable options (answers stored as option IDs)
const CHOICE_QUESTION_TYPES = [
  "single_choice",
  "multiple_choice", 
  "dropdown",
  "yes_no",
  "likert_scale",
];

// Only radio_grid and checkbox_grid support row_equals (column selection semantics)
const ROW_EQUALS_SUPPORTED_TYPES = ["radio_grid", "checkbox_grid"];

// Helper: Check if a question type supports row_equals operator
const supportsRowEquals = (q: QuestionType): boolean => {
  return ROW_EQUALS_SUPPORTED_TYPES.includes(q.type);
};

// Helper to format matrix value for display (ID-based only)
// Helper to format grid value for display (ID-based only)
// Note: Only applies to radio_grid and checkbox_grid question types
const formatGridValue = (
  value: unknown,
  question: QuestionType | undefined
): string => {
  if (!question || typeof value !== "object" || value === null || Array.isArray(value)) {
    return String(value || "");
  }
  
  const gridValue = value as { rowId: string; columnId: string };
  const rows = question.rows || [];
  const columns = question.columns || [];
  
  // ID-based lookup only
  const rowLabel = rows.find(r => r.id === gridValue.rowId)?.label || "?";
  const colLabel = columns.find(c => c.id === gridValue.columnId)?.label || "?";
  
  return `${rowLabel} = ${colLabel}`;
};

interface LogicBuilderProps {
  question: QuestionType;
  allQuestions: QuestionType[];
  onChange: (rules: SkipLogicRule[]) => void;
}

const OPERATORS: { value: LogicOperator; label: string }[] = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "does not contain" },
  { value: "in", label: "is one of" },
  { value: "not_in", label: "is not one of" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is not empty" },
  { value: "greater_than", label: "is greater than" },
  { value: "less_than", label: "is less than" },
  { value: "row_equals", label: "row equals" },
  { value: "row_not_equals", label: "row does not equal" },
];

const ACTIONS: { value: SkipAction; label: string; color: string }[] = [
  { value: "continue", label: "Continue to next", color: "text-green-600 dark:text-green-400" },
  { value: "skip_to_question", label: "Skip to question", color: "text-blue-600 dark:text-blue-400" },
  { value: "terminate", label: "Terminate survey", color: "text-destructive" },
  { value: "skip_to_end", label: "Skip to end", color: "text-orange-600 dark:text-orange-400" },
];

const TERMINATION_TYPES: { value: TerminationType; label: string }[] = [
  { value: "screen_out", label: "Screen Out" },
  { value: "thank_and_close", label: "Thank and Close" },
  { value: "quota_full", label: "Quota Full" },
  { value: "completed", label: "Completed" },
  { value: "disqualified", label: "Disqualified" },
];

export function LogicBuilder({ question, allQuestions, onChange }: LogicBuilderProps) {
  const [rules, setRules] = useState<SkipLogicRule[]>(question.skipLogic || []);
  
  // Get current question index
  const currentIndex = allQuestions.findIndex(aq => aq.id === question.id);
  
  // CONDITIONS can only reference PREVIOUS questions (answers already given)
  // This prevents self-referential and impossible logic
  const previousQuestions = allQuestions.filter((_, i) => i < currentIndex);
  
  // TARGETS can only reference FUTURE questions (forward-only jumps)
  const futureQuestions = allQuestions.filter((_, i) => i > currentIndex);
  
  // Helper: Check if a question type has selectable options
  // Include questions with allowOther even if no explicit options
  const isChoiceQuestion = (q: QuestionType): boolean => {
    const hasOptions = !!q.options && q.options.length > 0;
    const hasOther = !!q.allowOther;
    return CHOICE_QUESTION_TYPES.includes(q.type) && (hasOptions || hasOther);
  };
  
  // Helper: Get question by ID
  const getQuestionById = (questionId: string): QuestionType | undefined => {
    return allQuestions.find(q => q.id === questionId);
  };
  
  const updateRules = (newRules: SkipLogicRule[]) => {
    setRules(newRules);
    onChange(newRules);
  };
  
  const addRule = () => {
    // Default condition references FIRST previous question (not this question)
    const defaultQuestionId = previousQuestions[0]?.id || "";
    
    const newRule: SkipLogicRule = {
      conditions: [{
        questionId: defaultQuestionId,
        operator: "equals",
        value: "",
      }],
      conditionOperator: "AND",
      action: "continue",
    };
    updateRules([...rules, newRule]);
  };
  
  const removeRule = (index: number) => {
    updateRules(rules.filter((_, i) => i !== index));
  };
  
  const updateRule = (index: number, updates: Partial<SkipLogicRule>) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], ...updates };
    updateRules(newRules);
  };
  
  const addCondition = (ruleIndex: number) => {
    // Default condition references FIRST previous question (not this question)
    const defaultQuestionId = previousQuestions[0]?.id || "";
    
    const newRules = [...rules];
    newRules[ruleIndex].conditions.push({
      questionId: defaultQuestionId,
      operator: "equals",
      value: "",
    });
    updateRules(newRules);
  };
  
  const updateCondition = (ruleIndex: number, condIndex: number, updates: Partial<LogicCondition>) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions[condIndex] = {
      ...newRules[ruleIndex].conditions[condIndex],
      ...updates,
    };
    updateRules(newRules);
  };
  
  const removeCondition = (ruleIndex: number, condIndex: number) => {
    const newRules = [...rules];
    newRules[ruleIndex].conditions = newRules[ruleIndex].conditions.filter((_, i) => i !== condIndex);
    updateRules(newRules);
  };
  
  // First question cannot have condition-based skip logic (no previous answers to check)
  // But it CAN have option-level skip logic (e.g., "If No → terminate")
  const isFirstQuestion = currentIndex === 0;
  
  // Helper to truncate text
  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="space-y-4">
      {isFirstQuestion ? (
        <p className="text-sm text-muted-foreground italic">
          This is the first question - skip logic conditions cannot reference previous answers.
          Use option-level logic (in the Options tab) for routing based on this question's answer.
        </p>
      ) : rules.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No skip logic configured. Click "Add Skip Logic Rule" to create one.
        </p>
      ) : (
        <div className="space-y-4">
          {rules.map((rule, ruleIndex) => (
            <Card key={ruleIndex} className="border-border">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <span className="text-muted-foreground">Rule {ruleIndex + 1}</span>
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => removeRule(ruleIndex)}
                    aria-label={`Delete rule ${ruleIndex + 1}`}
                    className="h-8 w-8"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-3 px-4 space-y-4">
                {/* Conditions */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    When
                  </Label>
                  {rule.conditions.map((condition, condIndex) => (
                    <div key={condIndex} className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      {condIndex > 0 && (
                        <Select
                          value={rule.conditionOperator}
                          onValueChange={(v) => updateRule(ruleIndex, { conditionOperator: v as "AND" | "OR" })}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AND">AND</SelectItem>
                            <SelectItem value="OR">OR</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      
                      {/* Question Selector */}
                      <Select
                        value={condition.questionId}
                        onValueChange={(v) => updateCondition(ruleIndex, condIndex, { questionId: v })}
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Select question" />
                        </SelectTrigger>
                        <SelectContent>
                          {previousQuestions.length === 0 ? (
                            <SelectItem value="" disabled>
                              No previous questions to reference
                            </SelectItem>
                          ) : (
                            previousQuestions.map((q) => (
                              <SelectItem key={q.id} value={q.id}>
                                {q.originalQuestionNumber || `Q${allQuestions.findIndex(aq => aq.id === q.id) + 1}`}: {truncate(q.question, 30)}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      
                      {/* Operator Selector */}
                      <Select
                        value={condition.operator}
                        onValueChange={(v) => updateCondition(ruleIndex, condIndex, { 
                          operator: v as LogicOperator,
                          value: (v === "row_equals" || v === "row_not_equals") 
                            ? { rowId: "", columnId: "" } 
                            : ""
                        })}
                      >
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATORS
                            .filter(op => {
                              const selectedQ = getQuestionById(condition.questionId);
                              const isMatrixQuestion = selectedQ && supportsRowEquals(selectedQ);
                              
                              if (isMatrixQuestion) {
                                return ["row_equals", "row_not_equals", "is_not_empty", "is_empty", "any"].includes(op.value);
                              } else {
                                return op.value !== "row_equals" && op.value !== "row_not_equals";
                              }
                            })
                            .map(op => (
                              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Value Input (conditional) */}
                      {!["is_empty", "is_not_empty", "any"].includes(condition.operator) && (() => {
                        const selectedQuestion = getQuestionById(condition.questionId);
                        
                        // Grid question with row_equals/row_not_equals
                        if (selectedQuestion && supportsRowEquals(selectedQuestion) && 
                            (condition.operator === "row_equals" || condition.operator === "row_not_equals")) {
                          const rows = selectedQuestion.rows || [];
                          const columns = selectedQuestion.columns || [];
                          const rawValue = (typeof condition.value === "object" && condition.value !== null && !Array.isArray(condition.value))
                            ? condition.value as { rowId?: string; columnId?: string }
                            : {};
                          const currentRowId = rawValue.rowId || "";
                          const currentColumnId = rawValue.columnId || "";
                          
                          return (
                            <div className="flex gap-2">
                              <Select
                                value={currentRowId}
                                onValueChange={(v) => updateCondition(ruleIndex, condIndex, { 
                                  value: { rowId: v, columnId: currentColumnId }
                                })}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Select row" />
                                </SelectTrigger>
                                <SelectContent>
                                  {rows.map((row) => (
                                    <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              <span className="text-muted-foreground self-center">=</span>
                              
                              <Select
                                value={currentColumnId}
                                onValueChange={(v) => updateCondition(ruleIndex, condIndex, { 
                                  value: { rowId: currentRowId, columnId: v }
                                })}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Select column" />
                                </SelectTrigger>
                                <SelectContent>
                                  {columns.map((col) => (
                                    <SelectItem key={col.id} value={col.id}>{col.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }
                        
                        // Fallback for matrix value display
                        if (typeof condition.value === "object" && condition.value !== null && 
                            !Array.isArray(condition.value) && "rowId" in condition.value && "columnId" in condition.value) {
                          return (
                            <span className="text-sm text-muted-foreground px-2 py-1 bg-muted rounded">
                              {formatGridValue(condition.value, selectedQuestion)}
                            </span>
                          );
                        }
                        
                        // Choice question: dropdown of options
                        if (selectedQuestion && isChoiceQuestion(selectedQuestion)) {
                          const selectableOptions = getSelectableOptions(selectedQuestion);
                          return (
                            <Select
                              value={String(condition.value || "")}
                              onValueChange={(v) => updateCondition(ruleIndex, condIndex, { value: v })}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Select answer" />
                              </SelectTrigger>
                              <SelectContent>
                                {selectableOptions.map((opt) => (
                                  <SelectItem key={opt.id} value={opt.id}>
                                    {opt.text}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        }
                        
                        // Default: Text input
                        return (
                          <Input
                            value={String(condition.value || "")}
                            onChange={(e) => updateCondition(ruleIndex, condIndex, { value: e.target.value })}
                            placeholder="Enter value"
                            className="w-[180px]"
                          />
                        );
                      })()}
                      
                      {/* Remove Condition */}
                      {rule.conditions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeCondition(ruleIndex, condIndex)}
                          aria-label="Remove condition"
                          className="h-8 w-8 shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => addCondition(ruleIndex)}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Condition
                  </Button>
                </div>
                
                {/* Action */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Then
                  </Label>
                  <div className="space-y-3">
                    <Select
                      value={rule.action}
                      onValueChange={(v) => updateRule(ruleIndex, { action: v as SkipAction })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTIONS.map(action => (
                          <SelectItem key={action.value} value={action.value}>
                            <span className={action.color}>{action.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Skip to question selector */}
                    {rule.action === "skip_to_question" && (
                      <Select
                        value={rule.targetQuestionId || ""}
                        onValueChange={(v) => updateRule(ruleIndex, { targetQuestionId: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select target question" />
                        </SelectTrigger>
                        <SelectContent>
                          {futureQuestions.map((q) => (
                            <SelectItem key={q.id} value={q.id}>
                              {q.originalQuestionNumber || `Q${allQuestions.findIndex(aq => aq.id === q.id) + 1}`}: {truncate(q.question, 40)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Termination type selector */}
                    {rule.action === "terminate" && (
                      <>
                        <Select
                          value={rule.terminationType || "screen_out"}
                          onValueChange={(v) => updateRule(ruleIndex, { terminationType: v as TerminationType })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select termination type" />
                          </SelectTrigger>
                          <SelectContent>
                            {TERMINATION_TYPES.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          value={rule.terminationMessage || ""}
                          onChange={(e) => updateRule(ruleIndex, { terminationMessage: e.target.value })}
                          placeholder="Custom message (optional)"
                        />
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Add Rule Button */}
      <Button
        variant="outline"
        onClick={addRule}
        disabled={isFirstQuestion}
        title={isFirstQuestion ? "First question has no previous questions to reference" : undefined}
        className="w-full mt-4 border-dashed"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Skip Logic Rule
      </Button>
    </div>
  );
}
